import { NavLink } from "react-router-dom";
import React, { useState } from "react";
import styled from "@emotion/styled";
import gql from "graphql-tag";
import { useQuery } from "@apollo/client";
import { Below, Above } from "../UI/Helpers";
import Search from "./Search";
import { useLocation } from "react-router-dom";

const Nav = styled("nav")`
  height: 100%;
`;

const MenuButton = styled("li")`
  list-style: none;
  display: inline-flex;
  font-size: 0;
  position: relative;
  flex-grow: 1;
  align-items: center;
  height: 100%;
  ${(props) => props.theme.bp.below.lg} {
    width: 100%;
    display: flex;
    flex-direction: column;
    height: auto;
    margin-bottom: 1rem;
  }
  a,
  span {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    white-space: nowrap;
    padding: 0 0.5rem;
    height: 100%;
    text-align: center;
    text-decoration: none;
    color: black;
    font-weight: 400;
    letter-spacing: .5px;
    position: relative;
    font-family: "Lora", sans-serif;
    width: 100%;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;
    ${(props) => props.theme.bp.below.lg} {
      padding: 0.4rem 0;
      font-size: 1.8rem;
      line-height: 1.8rem;
      max-width: calc(100% - 3rem);
      display: block;
      margin: 0 1.5rem;
      text-align: left;
      text-transform: none;
      color: white !important;
      background: transparent !important;
    }
    ${({ theme }) => `
      .handel &, .köpcenter & { &.active, &:hover, &.open { color: ${theme.transColor(theme.colors.retail, 1)}; } }
      .fastigheter & { &.active, &:hover, &.open { color: ${theme.transColor(theme.colors.properties, 1)}; } }
      .investeringar & { &.active, &:hover, &.open { color: ${theme.transColor(theme.colors.investments, 1)}; } }
    `}
    &:hover,
    &.open {
      ${(props) => props.theme.bp.above.lg} {
        background: white;
        color: ${(props) => props.theme.colors.primary};
      }
    }
    &.active {
      ${(props) => props.theme.bp.above.lg} {
        background: white;
        color: ${(props) => props.theme.colors.primary};
      }
    }
    ${(props) => props.theme.bp.below.lg} {
      &:hover,
      &.active,
      &.open {
        color: white !important;
        background: transparent;
      }
    }
    i {
      margin-left: 0.5rem;
      font-size: 0.6em;
      transition: transform 0.2s ease;
    }
    &.open i {
      transform: rotate(180deg);
    }
  }
  ul.subMenu {
    position: absolute;
    padding: 0;
    background: white;
    z-index: 2;
    top: 100%;
    left: 0;
    min-width: 100%;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    border-radius: 0 0 4px 4px;
    display: flex;
    flex-direction: column;
    ${(props) => props.theme.bp.below.lg} {
      background: transparent;
      position: static;
      box-shadow: none;
      border-radius: 0;
      padding: 0;
      margin-bottom: .5rem;
    }
    li {
      width: 100%;
      list-style: none;
    }
    a {
      padding: 0.75rem 1.5rem;
      text-align: left;
      white-space: nowrap;
      display: block;
      width: auto;
      height: auto;
      font-size: 14px;
      font-weight: 400;
      transition: background-color 0.2s ease, color 0.2s ease;
      &.active {
        background-color: ${(props) => props.theme.colors.lightGray};
        color: ${(props) => props.theme.colors.primary};
      }
      &:hover:not(.active) {
        background-color: ${(props) => props.theme.colors.lightGray} !important;
      }
      ${(props) => props.theme.bp.below.lg} {
        padding: 0.4rem 0;
        background: none !important;
        font-weight: 200;
        color: white !important;
        font-size: 1.1rem;
        padding: .4rem 0;
        &:hover,
        &.active {
          background: none !important;
          color: white !important;
        }
      }
    }
  }
`;

const MenuWrapper = styled("ul")`
  position: relative;
  height: 100%;
  padding: 0;
  margin: 0;
  display: flex;
  justify-content: space-between;
  padding-left: 1rem;
  .handel &,
  .köpcenter & {
    background: ${(props) => props.theme.colors.retail};
  }
  .fastigheter & {
    background: ${(props) => props.theme.colors.properties};
  }
  .investeringar & {
    background: ${(props) => props.theme.colors.investments};
  }
  ${(props) => props.theme.bp.below.lg} {
    background: ${(props) => props.theme.colors.primary} !important;
    flex-wrap: wrap;
    padding: 0 0.5rem;
    transform: translateX(${(props) => (props.open ? "0" : "100%")});
    width: 100vw;
    height: 100vh;
    position: fixed;
    top: 0;
    right: 0;
    align-content: flex-start;
    padding-top: 5rem;
    justify-items: flex-start;
    z-index: 9;
    overflow-y: auto;
  }
  transition: transform 0.3s ease-in-out;
`;

const MenuToggle = styled("button")`
  position: fixed;
  top: 1rem;
  right: 1rem;
  font-size: 1.7rem;
  width: 3.5rem;
  height: 3.5rem;
  padding: 0.5rem;
  color: white;
  border: none;
  outline: none;
  background: ${(props) => props.theme.colors.primary};
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  svg {
    width: 24px;
    height: 24px;
    fill: white;
  }
`;

const SearchToggle = styled(MenuButton)`
  width: 60px;
  max-width: 60px;
  text-align: center;

  ${(props) => props.theme.bp.below.lg} {
    display: none;
  }

  span {
    svg {
      width: 20px;
      height: 20px;
    }
  }
  &.isActive {
    background: white;
    span {
      color: ${(props) => props.theme.colors.primary};
      .handel &,
      .köpcenter & {
        color: ${(props) =>
          props.theme.transColor(props.theme.colors.retail, 0.7)};
      }
      .fastigheter & {
        color: ${(props) =>
          props.theme.transColor(props.theme.colors.properties, 0.7)};
      }
      .investeringar & {
        color: ${(props) =>
          props.theme.transColor(props.theme.colors.investments, 0.7)};
      }
    }
  }
`;

// Define styled component for mobile MyPagesLink
const MobileMyPagesLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 1.5rem 1.5rem 0;
  padding: 0.5rem 1rem 0.5rem 0.5rem;
  text-align: left;
  text-decoration: none;
  font-family: ${(props) => props.theme.fonts.headings};
  font-size: 1.1rem;
  font-weight: 400;
  color: white;
  background-color: transparent;
  border-radius: 100px;
  border: 1px solid white;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
  }

  .icon {
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    svg {
      width: 12px;
      height: 12px;
      path {
        fill: ${(props) => props.theme.colors.primary};
      }
    }
  }
  .text {
    
  }
`;

const MenuButtonWrapper = ({ submenu, setSubmenu, toggleMenu, ...props }) => {
  const hasChildren = props.children && props.children.length > 0;
  const className = submenu === props.title ? "open" : "closed";
  const location = useLocation();

  const isActive = location.pathname === props.url || 
                   (hasChildren && props.children.some(child => location.pathname === child.url));

  return (
    <MenuButton>
      <NavLink
        className={`${className} ${isActive ? 'active' : ''}`}
        to={props.url}
        onClick={(e) => {
          if (hasChildren) {
            e.preventDefault();
            setSubmenu(submenu === props.title ? null : props.title);
          } else {
            setSubmenu(null);
            toggleMenu();
          }
        }}
      >
        {props.title}
        {hasChildren && <i className="fas fa-chevron-down"></i>}
      </NavLink>
      {hasChildren && submenu === props.title && (
        <SubMenu items={props.children} setSubmenu={setSubmenu} toggleMenu={toggleMenu} />
      )}
    </MenuButton>
  );
};

const MenuContent = (props) => {
  const { menu, activeSubmenu, setSubmenu, toggleSearch, toggleMenu } = props;
  
  return (
    <>
      {menu.items.map((item, i) => (
        <MenuButtonWrapper
          key={i}
          {...item}
          submenu={activeSubmenu}
          setSubmenu={setSubmenu}
          toggleMenu={toggleMenu}
        />
      ))}
      <Below size="lg">
        <Search toggleSearch={toggleSearch} toggleMenu={toggleMenu}/>
        <MobileMyPagesLink href="https://orvelin.realportal.nu/common/portal.php" target="_blank" rel="noopener noreferrer">
          <span className="icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M224 256A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-45.7 48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512l388.6 0c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304l-91.4 0z"/></svg>
          </span>
          <span className="text">
            Mina sidor
          </span>
        </MobileMyPagesLink>
      </Below>
    </>
  );
};

const SubMenu = (props) => {
  const { items, setSubmenu, toggleMenu } = props;
  return (
    <ul className="subMenu">
      {items.map((item, i) => (
        <li key={i}>
          <NavLink 
            to={item.url}
            onClick={() => {
              setSubmenu(null);
              toggleMenu();
            }}
          >
            {item.title}
          </NavLink>
        </li>
      ))}
    </ul>
  );
};

const MenuLoading = () => (
    <MenuWrapper>
        <li>Loading...</li> 
    </MenuWrapper>
);

const Menu = () => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);

  const toggleSearch = () => setSearchOpen(!searchOpen);
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
    if (menuOpen) { 
      setSearchOpen(false); 
      setActiveSubmenu(null); 
    }
  };
  const setSubmenu = (title) => setActiveSubmenu(title);

  const { loading, error, data } = useQuery(menuQuery);

  if (error) {
    console.error("Error fetching menu:", error);
  }

  return (
    <Nav>
      <Below size="lg">
        <MenuToggle onClick={toggleMenu} open={menuOpen}>
          {menuOpen ? (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          )}
        </MenuToggle>
      </Below>
      <MenuWrapper open={menuOpen}>
        {loading && <MenuLoading />} 
        {data && data.menu && (
          <MenuContent
            menu={data.menu}
            activeSubmenu={activeSubmenu}
            setSubmenu={setSubmenu}
            toggleSearch={toggleSearch}
            searchOpen={searchOpen}
            toggleMenu={toggleMenu}
          />
        )}
        <Above size="lg">
          <SearchToggle className={searchOpen ? "isActive" : ""}>
            <span onClick={toggleSearch}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M384 208A176 176 0 1 0 32 208a176 176 0 1 0 352 0zM343.3 366C307 397.2 259.7 416 208 416C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208c0 51.7-18.8 99-50 135.3L507.3 484.7c6.2 6.2 6.2 16.4 0 22.6s-16.4 6.2-22.6 0L343.3 366z"/></svg>
            </span>
          </SearchToggle>
        </Above>
        {searchOpen && <Search toggleSearch={toggleSearch} toggleMenu={toggleMenu}/>}
      </MenuWrapper>
    </Nav>
  );
};

const menuQuery = gql`
  {
    menu(position: "header") {
      items {
        title
        url
        children {
          title
          url
        }
      }
    }
  }
`;

export default Menu;
